import { Select } from "@mui/material";
import { useThemeColors } from "../context/ThemeContext";

/**
 * AppSelect — the app's single reusable dropdown.
 *
 * Wraps MUI <Select> and bakes in the standard field styling plus a fully
 * OPAQUE menu paper (colors.menuBg). The default MUI / glass surfaces are
 * translucent, which made open dropdowns look see-through; this component
 * guarantees the popup is solid so page content never bleeds through.
 *
 * Props: same as MUI <Select>, plus
 *   - menuSx:   extra sx merged onto the menu paper (e.g. per-use sizing)
 *   - MenuProps: merged on top (e.g. anchorOrigin/transformOrigin for an
 *                upward-opening menu)
 */
function AppSelect({ sx, menuSx, MenuProps, children, ...props }) {
  const colors = useThemeColors();

  const baseSx = {
    fontSize: 13,
    color: colors.text,
    background: colors.cardBg,
    borderRadius: 2,
    "& .MuiOutlinedInput-notchedOutline": { borderColor: colors.cardBorder },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: colors.cardBorderHover,
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#818cf8" },
    "& .MuiSvgIcon-root": { color: colors.textMuted },
  };

  const paperSx = {
    background: colors.menuBg,
    backgroundColor: colors.menuBg,
    backdropFilter: "none",
    border: `1px solid ${colors.cardBorder}`,
    boxShadow: colors.shadowLg,
    borderRadius: 2,
    "& .MuiList-root": { py: 0.5 },
    "& .MuiMenuItem-root": {
      fontSize: 13,
      color: colors.text,
      mx: 0.5,
      my: 0.25,
      borderRadius: 1,
      "&:hover": { background: colors.hover },
      "&.Mui-selected": {
        background: "rgba(129, 140, 248, 0.14)",
        color: "#818cf8",
        fontWeight: 700,
        "&:hover": { background: "rgba(129, 140, 248, 0.2)" },
      },
    },
    ...menuSx,
  };

  const mergedMenuProps = {
    ...MenuProps,
    slotProps: { paper: { sx: paperSx } },
  };

  return (
    <Select
      size="small"
      sx={{ ...baseSx, ...sx }}
      MenuProps={mergedMenuProps}
      {...props}
    >
      {children}
    </Select>
  );
}

export default AppSelect;
